<template>
  <div>
    <form @submit.prevent="handlePayment">
      <TextInput
        v-model="paymentForm.cardHolderName"
        size="large"
        placeholder="Cardholder Name"
        class="mb-4"
        data-qa="payment-card-name"
        label="Cardholder Name"
        :error="fieldErrors.first_name || fieldErrors.last_name"
      />
      <div class="flex">
        <TextInput
          v-model="paymentForm.billingAddress"
          size="large"
          class="mb-4 mr-4 w-3/4"
          data-qa="payment-billing-address"
          label="Billing Address"
          placeholder="123 Street"
          autocomplete="street-address"
          :error="fieldErrors.address1"
        />
        <TextInput
          v-model="paymentForm.suite"
          size="large"
          class="mb-4 w-1/4"
          data-qa="payment-suite"
          label="Suite"
          placeholder="#"
        />
      </div>
      <div class="flex">
        <TextInput
          v-model="paymentForm.city"
          size="large"
          class="mb-4 mr-4 w-1/2"
          data-qa="payment-city"
          label="City"
          placeholder="City"
          autocomplete="address-level2"
          :error="fieldErrors.city"
        />
        <SelectInput
          v-model="paymentForm.state"
          size="large"
          :options="stateOptions"
          class="mb-4 w-1/2"
          data-qa="payment-state"
          label="State"
          placeholder="State"
          autocomplete="address-level1"
          :show-reset="false"
          :error="fieldErrors.state"
        />
      </div>
      <div class="flex">
        <TextInput
          v-model="paymentForm.zip"
          size="large"
          class="mb-4 mr-4 w-1/2"
          data-qa="payment-zip"
          label="ZIP"
          placeholder="ZIP"
          autocomplete="postal-code"
          :error="fieldErrors.postal_code"
        />
        <SelectInput
          v-model="paymentForm.country"
          size="large"
          :options="Countries"
          class="mb-4 w-1/2"
          data-qa="payment-country"
          label="Country"
          placeholder="Country"
          autocomplete="country"
          :show-reset="false"
          :error="fieldErrors.country"
        />
      </div>

      <label for="payment-card-field" class="font-bold mb-2 block">
        Credit Card Number
      </label>
      <!--
        The Recurly card element must stay mounted, so this wrapper never sits
        behind a v-if/v-show and carries no Tailwind display class.
      -->
      <div
        class="credit_card_wrapper mb-4"
        :class="{ 'is-error': declined }"
        data-qa="recurly-card"
      >
        <div id="payment-card-field" ref="cardMount" class="px-4 w-full" />
      </div>

      <BaseBanner :show-close="false" icon="lock" variant="explainer">
        <TypeBody variant="small">
          Bambee <b><u>only</u></b> accepts credit cards. As an added security
          measure, debit and pre-paid cards are not accepted.
        </TypeBody>
      </BaseBanner>

      <BaseDivider class="my-4" />

      <BaseBanner
        v-if="loadError"
        variant="error"
        class="mb-2"
        :show-close="false"
        data-qa="recurly-load-error"
      >
        {{ loadError }}
      </BaseBanner>
      <BaseBanner
        v-if="shownError"
        variant="error"
        class="mb-2"
        @close="localError = ''"
      >
        {{ shownError }}
      </BaseBanner>
      <TypeBody
        v-if="!isAddressComplete"
        variant="small"
        class="mb-2 text-gray-2"
        data-qa="payment-address-hint"
      >
        Enter the billing street address, city, state, ZIP and country to
        continue.
      </TypeBody>
      <BaseButton
        v-if="!hideBtn"
        class="mb-2 bg-purple-gradient"
        :post-icon="isBusy || paymentSuccess ? '' : 'arrowRight'"
        size="giant"
        variant="primary"
        block
        type="submit"
        data-qa="payment-submit"
        :disabled="
          isBusy || paymentSuccess || !!loadError || !isAddressComplete
        "
      >
        <span v-if="isBusy" class="flex items-center">
          Subscribing
          <AnnularThrobber size="sm" class="ml-2" />
        </span>
        <span v-else>{{ btnTitle }}</span>
      </BaseButton>
    </form>

    <div class="mt-6 flex flex-col gap-4">
      <TypeBody variant="small">
        By clicking "{{ btnTitle }}", you agree to our Terms & Conditions &
        Privacy Policy
      </TypeBody>
      <SecuredDisclaimer class="pb-0" />
    </div>
  </div>
</template>

<script>
import {
  BaseBanner,
  BaseButton,
  TextInput,
  BaseDivider,
  AnnularThrobber,
  TypeBody,
  SelectInput,
} from '@bambeehr/pollen';
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  reactive,
} from '@nuxtjs/composition-api';

import SecuredDisclaimer from '@/components/SecuredDisclaimer/SecuredDisclaimer';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import UsStates from '@/constants/UsStates';
import { Countries } from '@/constants/Countries';
import { getRecurly, tokenizeCard } from '@/services/recurly';

// Recurly.js field names that belong to the card element rather than an input of ours.
const CARD_FIELDS = ['number', 'month', 'year', 'cvv'];

/**
 * Turns a card into a Recurly.js token id (BIO-1869/1870/1871), ported from app's
 * RecurlyPaymentForm. Card details go straight from the browser to Recurly; the parent only
 * ever sees the token. Emits `submit` with `{ tokenId, method: 'card', cardHolderName }`.
 *
 * The billing address is required for the token: Recurly validates and taxes against it, so
 * submit stays disabled until street, city, state, ZIP and country are filled.
 */
export default {
  name: 'PaymentForm',

  components: {
    BaseBanner,
    BaseButton,
    TextInput,
    BaseDivider,
    AnnularThrobber,
    SecuredDisclaimer,
    TypeBody,
    SelectInput,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    btnTitle: {
      type: String,
      default: 'Start Trial',
    },
    paymentSuccess: {
      type: Boolean,
      default: false,
    },
    hideBtn: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { state: registrationState } = useRegistration();
    const paymentForm = reactive({
      cardHolderName: '',
      billingAddress: '',
      suite: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
    });
    const fullName = computed(
      () => registrationState[RegistrationStateNames.FULL_NAME]
    );
    // Prefill once registration state has hydrated; never overwrite what the lead typed.
    watch(
      fullName,
      (name) => {
        if (name && !paymentForm.cardHolderName) {
          paymentForm.cardHolderName = name;
        }
      },
      { immediate: true }
    );

    // Two-letter codes, which is what Recurly expects in `state`.
    const stateOptions = Object.entries(UsStates).map(([value, label]) => ({
      label,
      value,
    }));

    const isAddressComplete = computed(() =>
      [
        paymentForm.billingAddress,
        paymentForm.city,
        paymentForm.state,
        paymentForm.zip,
        paymentForm.country,
      ].every((value) => !!`${value || ''}`.trim())
    );

    const cardMount = ref(null);
    const loadError = ref('');
    const localError = ref('');
    const fieldErrors = ref({});
    const declined = ref(false);
    const tokenizing = ref(false);
    const isBusy = computed(() => tokenizing.value || props.loading);
    const shownError = computed(() => localError.value || props.errorMessage);
    let recurly = null;
    let elements = null;
    let cardElement = null;

    onMounted(async () => {
      try {
        recurly = await getRecurly();
        elements = recurly.Elements();
        cardElement = elements.CardElement({ style: { fontSize: '16px' } });
        cardElement.attach(cardMount.value);
      } catch (e) {
        loadError.value = e?.message || 'Payments are unavailable right now.';
      }
    });

    onBeforeUnmount(() => {
      cardElement?.remove?.();
    });

    const handlePayment = async () => {
      if (!recurly || isBusy.value || !isAddressComplete.value) {
        return;
      }
      tokenizing.value = true;
      localError.value = '';
      fieldErrors.value = {};
      declined.value = false;

      const [firstName, ...rest] = paymentForm.cardHolderName
        .trim()
        .split(/\s+/);
      try {
        const token = await tokenizeCard(recurly, elements, {
          first_name: firstName,
          last_name: rest.join(' '),
          address1: paymentForm.billingAddress,
          address2: paymentForm.suite,
          city: paymentForm.city,
          state: paymentForm.state,
          postal_code: paymentForm.zip,
          country: paymentForm.country,
        });

        emit('submit', {
          tokenId: token.id,
          method: 'card',
          cardHolderName: paymentForm.cardHolderName,
        });
      } catch (err) {
        const fields = err?.fields || [];
        declined.value = fields.some((f) => CARD_FIELDS.includes(f));
        fieldErrors.value = Object.fromEntries(
          fields
            .filter((f) => !CARD_FIELDS.includes(f))
            .map((f) => [f, `${f.replace(/_/g, ' ')} is invalid`])
        );
        localError.value =
          err?.message || 'Please verify your payment information';
      }
      tokenizing.value = false;
    };

    return {
      cardMount,
      Countries,
      declined,
      fieldErrors,
      handlePayment,
      isAddressComplete,
      isBusy,
      loadError,
      localError,
      paymentForm,
      shownError,
      stateOptions,
    };
  },
};
</script>

<style scoped>
/* The 54px bordered wrapper Striper.vue drew around the Stripe card element. */

.credit_card_wrapper {
  align-items: center;
  background-color: #fff;
  border: 2px solid #dfdee0;
  border-radius: 5px;
  display: flex;
  height: 54px;
}

.credit_card_wrapper.is-error {
  border-color: red;
}
</style>
